import { Button } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import styled from 'styled-components';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';

export const StyledCard = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	border-radius: 0.25rem;
	overflow: hidden;
`;

export const Header = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
`;

export const HeaderTop = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem 1rem;
`;

export const HeaderBottom = styled.div`
	display: grid;
	grid-template-columns: 3fr 8fr 4fr 1fr;
`;

export const SwitchContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;

	.mantine-Switch-root {
		.mantine-Switch-body {
			input:checked + * > .mantine-Switch-thumb {
				left: calc(100% - 1.125rem);
			}

			.mantine-Switch-track {
				background-color: ${({ theme }) => theme.v3.colors.blue[2]} !important;
				border-color: ${({ theme }) => theme.v3.colors.blue[6]} !important;

				.mantine-Switch-thumb {
					background-color: ${({ theme }) => theme.v3.colors.blue[6]} !important;
				}

				.mantine-Switch-trackLabel {
					background-color: ${({ theme }) => theme.v3.colors.blue[2]} !important;
				}
			}
		}
	}
`;

export const SwitchLabel = styled.div<{ $active: boolean }>`
	cursor: pointer;
	user-select: none;
	color: ${({ theme, $active }) =>
		$active ? theme.v2.color.secondary[800] : theme.v3.colors.dark[8]};
	font-size: 0.875rem;
	font-weight: 500;
`;

export const Table = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

export const TableHeader = styled.div`
	display: grid;
	grid-template-columns: 3fr 8fr 4fr 1fr;
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
`;

export const TableRow = styled.div<{
	$isHeader?: boolean;
	$isFooter?: boolean;
	$disabled?: boolean;
}>`
	display: grid;
	grid-template-columns: 3fr 8fr 4fr 1fr;
	border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	background-color: ${({ theme, $isHeader, $isFooter }) =>
		$isHeader || $isFooter ? theme.v2.color.neutral[100] : 'transparent'};

	opacity: ${({ $disabled }) => ($disabled ? 0.2 : 1)};

	&:first-of-type {
		background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	}

	&:last-child {
		border-bottom: none;
	}
`;

export const TableCell = styled.div<{ $hasBorder?: boolean }>`
	display: flex;
	align-items: start;
	height: 100%;
	width: 100%;
	border-right: ${({ theme, $hasBorder = true }) =>
		$hasBorder ? `1px solid ${theme.v2.color.neutral[300]}` : 'none'};

	&:last-child {
		border-right: none;
	}
`;

export const TableCellContent = styled.div<{
	$hasInputs?: boolean;
	$minWidth?: string;
}>`
	width: 100%;
	display: flex;
	align-items: center;
	padding: ${({ $hasInputs }) => ($hasInputs ? '0' : '1rem')};
	min-width: ${({ $minWidth }) => $minWidth};
`;

export const StyledButton = styled((props) => <Button variant="outlined" icon="plus" {...props} />)`
	background: none;
	border: 1px solid ${({ theme }) => theme.v2.color.secondary[800]};
	&& {
		padding: 0.25rem 0.75rem;
	}
	height: auto;
	min-height: 0;
	color: ${({ theme }) => theme.v2.color.secondary[800]};
	font-size: 0.875rem;
	display: flex;
	align-items: center;
	cursor: pointer;
	font-weight: 600;

	&:hover {
		background: none;
		color: ${({ theme }) => theme.v2.color.secondary[800]};
		border-color: ${({ theme }) => theme.v2.color.secondary[800]};
	}

	${Icon} {
		color: ${({ theme }) => theme.v2.color.secondary[800]};
	}
`;

export const FlexContainer = styled.div<{ $direction?: 'row' | 'column'; $gap?: string }>`
	display: flex;
	flex-direction: ${({ $direction = 'column' }) => $direction};
	align-items: ${({ $direction = 'column' }) =>
		$direction === 'column' ? 'flex-start' : 'center'};
	width: 100%;
	gap: ${({ $gap }) => $gap};
`;

export const RowContainer = styled.div<{ $showLastChildBorder?: boolean }>`
	width: 100%;
	border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};

	&:last-child {
		border-bottom: ${({ theme, $showLastChildBorder }) =>
			$showLastChildBorder ? `1px solid ${theme.v2.color.neutral[300]}` : 'none'};
	}
`;

export const AdjustmentRow = styled.div<{
	$variant?: 'input' | 'total' | 'header';
	$columns?: number;
	$gap?: string;
	$customHeight?: string;
}>`
	display: ${({ $variant = 'input' }) => ($variant === 'input' ? 'grid' : 'flex')};
	${({ $variant = 'input', $columns = 2 }) =>
		$variant === 'input' ? `grid-template-columns: ${`repeat(${$columns}, 1fr)`};` : ''};
	align-items: center;
	justify-content: ${({ $variant }) => ($variant === 'total' ? 'flex-end' : 'flex-start')};
	height: ${({ $customHeight }) => $customHeight ?? '3.5rem'};
	width: 100%;
	gap: ${({ $gap }) => $gap ?? '0.5rem'};
	padding: 0.5rem 1rem;
`;

export const HeaderFooterRowItem = styled(AdjustmentRow).attrs({ $variant: 'header' })``;

export const InputAdjustmentRowTotal = styled(AdjustmentRow).attrs({ $variant: 'total' })``;

export const InputAdjustmentRowItem = styled(AdjustmentRow).attrs({
	$variant: 'input',
	$columns: 2,
})`
	.mantine-TextInput-input,
	.mantine-Select-input {
		margin: 0;
	}

	.mantine-ActionIcon-root {
		.svg {
			color: ${({ theme }) => theme.v2.color.error[800]};
		}

		&:disabled {
			border: none;
			background-color: ${({ theme }) => theme.v2.color.white[500]};

			.svg {
				color: ${({ theme }) => theme.v2.color.white[500]};
			}
		}
	}
`;

export const InputAdjustmentRowConvertedAmount = styled.div`
	display: flex;
	justify-content: flex-end;
	width: 100%;
`;

export const InputRow = styled.div`
	display: flex;
	align-items: flex-end;
	width: 363px;
`;

export const TotalRow = styled(AdjustmentRow).attrs({ $variant: 'input' })`
	justify-content: flex-start;
	padding-left: 1rem;
`;

export const ActionIconSizedPlaceholder = styled.div`
	width: 28px;
	height: 28px;
`;

export const TableHeaderTitle = styled(TextV2)`
	color: ${({ theme }) => theme.v2.color.neutral[600]};
`;
